<template>
  <st-scrolly class="waterfall-chart-container" :window-top="windowTop" :window-height="windowHeight" :trigger-offset="-100" v-if="data">
    <template v-slot:background="{slideIndex}">
      <div>
        <div class="waterfall-title"><h2>How quickly did schools get filled up in 2020?</h2></div>
        <waterfall-chart
          :data="data"
          :highlighted="highlighted(clamp(slideIndex, 0, slides.length - 1))"
          :focus="slides[clamp(slideIndex, 0, slides.length - 2)].phase">
        </waterfall-chart>
      </div>
    </template>

    <!-- slides -->
    <div class="waterfall-slide" v-for="item in slides">
      <div class="waterfall-bubble-text" v-if="item.phase">
        Schools popular in <span>Phase {{(item.phasename)? item.phasename : item.phase}}</span> included:
        <ul><li v-for="school in item.highlighted">{{school | capitalize}}</li></ul>
        <span class="opaque">{{item.explanation}}</span>
      </div>

      <div class="waterfall-selection"  :class="{'desktop-only': isDesktop, 'mobile-only': !isDesktop}" v-else>
        <label>Choose a school below to see its take-up rate as compared with the average.</label>
        <ul v-if="isDesktop">
          <li v-for="school in schools"
            :class="{active: selected === school}"
            @click="selected = school">
            {{school.label}}
          </li>
        </ul>
        <v-select v-else
          v-model="selected"
          :options="schools"
          :clearable="false">
        </v-select>
      </div>
    </div>
  </st-scrolly>
</template>

<script>
import StScrolly, {clamp} from '@st-graphics/scrolly'
import WaterfallChart from './WaterfallChart.vue'
import vSelect from 'vue-select'

export default {
  components: {
    StScrolly,
    WaterfallChart,
    vSelect
  },
  filters: {capitalize},
  props: ['attributes'],
  data () {
    return {
      data: null,
      schools: null,
      index: 0,
      selected: null
    }
  },
  computed: {
    slides  () {
      return this.attributes.value
    },
    phases () {
      return this.slides.filter(item => item.phase).map(item => item.phase)
    },
    isDesktop () {
      return this.$window.innerWidth >= 768
    },
    windowHeight () {
      return this.isDesktop ? 600 : this.$window.innerHeight
    },
    windowTop () {
      return (this.$window.innerHeight - this.windowHeight) / 2
    }
  },
  methods: {
    clamp,
    highlighted (index) {
      return this.slides[index].highlighted || [this.selected && this.selected.value]
    }
  },
  created () {
    const url = 'https://www.straitstimes.com/multimedia/graphics/json/waterfall.json'
    fetch(url).then(res => res.json()).then(data => {
      const groups = ['1', '2A', '2B', '2C']
      this.schools = []
      this.data = []
      data['2020'].forEach(d => {
        if (Object.keys(d).length <= 1) return
        let y = 0
        this.data.push({x: '0', y, g: d.id})
        groups.forEach(x => {
          y += d[x]
          this.data.push({x, y, g: d.id})
        })
        if (d.id !== '__OVERALL__') {
          this.schools.push({label: capitalize(d.id), value: d.id})
        }
      })
      this.selected = this.schools[0]
    })
  }
}

function capitalize (str) {
  return str
    .split(' ')
    .map(token => token[0].toUpperCase() + token.slice(1).toLowerCase())
    .map(token => token.replace(/^Chij$/, 'CHIJ'))
    .map(token => token.replace(/^Anglo-chinese$/, 'Anglo-Chinese'))
    .map(token => token.replace(/^\(([a-z])/, (match, p1) => '(' + p1.toUpperCase()))
    .join(' ')
}
</script>

<style lang="scss">
.waterfall-title {
  font-family: $font-SelaneForty;
  position: relative;
  height: 60px;
  padding-top: 12px;

  h2 {
    padding: 3px 0;
    font-family: $font-SelaneTextRegular;
    color: #333;
  }

  @media (min-width: $d_medium) {
    padding-top: 0;
  }
}

.waterfall-slide {
  height: 600px;
  padding: 60px 0 0 70%;

  @media (max-width: $d_medium - 1) {
    height: 800px;
    padding: calc(80vw + 80px) 0 0;
  }

  &:last-child {
    height: 600px;

    @media (max-width: $d_medium - 1) {
      height: 650px;
    }
  }
}

.waterfall-bubble-text,
.waterfall-selection {
  font-family: $font-SelaneTextRegular;
  font-size: 20px;
  line-height: 27px;
  color: $black;
}

.waterfall-bubble-text {
  padding: 1em;
  font-family: $font-SelaneTextRegular;
  font-size: 20px;
  color: $black;
  background-color: transparentize(#FFFFFF, 0.1);
  border: 1px solid $shadowColor;
  line-height: 27px;

  @media (max-width: $d_medium - 1) {
    padding: 0.5em 1em;
  }

  ul {
    margin: 1em 0;
    list-style: '- ' inside;

    @media (max-width: $d_medium - 1) {
      margin: 0.5em 0;
    }
  }

  li {
    margin-bottom: 0;
    margin-left: 1em;
    list-style-type: disc;
    font-family: $font-CuratorRegular;
    font-size: 16px;
    line-height: 24px;
    opacity: 0.6;
  }

  span {
    font-family: $font-SelaneForty;
  }

  .opaque {
    font-family: $font-SelaneTextRegular;
    font-size: 20px;
    line-height: 27px;
  }
}

.waterfall-selection {
  pointer-events: all;
  padding: 0 1em;

  label {
    font-family: $font-CuratorBold;
    font-size: 16px;
    line-height: 20px;
    display: block;
    color: #333;
    height: 50px;
  }

  &.desktop-only {
    margin-top: - 60px;
    overflow: hidden;

    label {
      height: 80px;
    }

    ul {
      background: #FFFFFF;
      list-style: none;
      height: 432px;
      overflow-x: hidden;
      overflow-y: auto;
      border: 1px solid #B7B7B7;
      border-radius: 5px;
    }

    li {
      background-color: #FFF;
      padding: 6px 6px 4px;
      border-bottom  : 1px dotted #a7aaaa;
      margin-bottom: 0;
      line-height: 25px;
      max-height: 24px;
      overflow: hidden;
      text-overflow: ellipsis;
      cursor: pointer;
      transition: transform 300ms;
      font-family: $font-CuratorRegular;
      font-size: 15px;

      &.active {
        font-family: $font-CuratorBold;
        background: $shadowColor;
        color: $themeColor;
      }

      &:hover {
        background: $shadowColor;
        color: $themeColor;
      }
    }
  }

  &.mobile-only {
    display: none;
    width: 100%;
    padding: 0;

    @media (max-width: $d_medium - 1) {
      display: block;
    }

    label {
      height: auto;
      margin: 1em 0;
    }

    .selected-tag {
      font-family: $font-CuratorRegular;
      font-size: 15px;
    }

    .dropdown-menu {
      position: static;
    }
  }
}
</style>
